import type { ReactNode } from "react";

/**
 * The composer's over-limit alert, host-owned status/metadata slots and
 * context notice — sat above the input box (PB-5422 chat-parts-private).
 * Split into a sibling of PlayerAIComposer.tsx to keep that file under the
 * 200-line soft cap (packages/ui/CLAUDE.md invariant 6). Moved as-is: no
 * class, DOM or behaviour change — renders nothing (as before) when none of
 * its inputs have anything to show.
 */
export function ComposerNotices({
  overLimit,
  composedLength,
  maxLength,
  statusSlot,
  metadataSlot,
  contextNotice,
}: {
  overLimit: boolean;
  composedLength: number;
  maxLength: number | undefined;
  statusSlot?: ReactNode | undefined;
  metadataSlot?: ReactNode | undefined;
  contextNotice?: string | undefined;
}) {
  if (!overLimit && !statusSlot && !metadataSlot && !contextNotice) {
    return null;
  }
  // The over-limit alert comes first: it is the only visible reason Send is
  // disabled for it. The status slot follows (a pending upload or an access
  // problem also blocks Send), then the attachment lines, then the widget-
  // attachment notice. Spacing lives INSIDE the scroller so the region can
  // shrink to nothing.
  return (
    <div data-composer-region className="-mx-1 -mt-1 min-h-0 overflow-y-auto">
      <div className="space-y-1.5 px-1 pt-1 pb-2">
        {overLimit ? (
          <p role="alert" className="text-xs text-pb-red-text">
            This message is {composedLength.toLocaleString()} characters, over
            the {maxLength?.toLocaleString()}
            -character limit. Shorten the pasted context or remove a pill —
            nothing has been cut for you.
          </p>
        ) : null}
        {statusSlot ? <div>{statusSlot}</div> : null}
        {metadataSlot ? <div>{metadataSlot}</div> : null}
        {contextNotice ? (
          <p role="status" className="text-xs text-pb-text-muted">
            {contextNotice}
          </p>
        ) : null}
      </div>
    </div>
  );
}
